<template>
  <div class="w-full-75">
    <AwesomeLoader
      :visible.sync="loading"
      :fullscreen="false"
      :darkMode="false"
      :message="loaderMessage"
      :primaryColor="getSpinPrimaryColor"
      :secondaryColor="getSpinSecondaryColor"
    />

    <el-card class="register-card" v-if="pageType === 1 && !loading">
      <div class="logo-container">
        <img :src="getLogoInfo" alt="Kodefast Logo" class="logo" :style="getImageBackground" />
        <p class="sub-title">Verify Your Email with OTP</p>
      </div>

      <el-form :model="form" :rules="rules" ref="formRef" label-position="top">
        <el-form-item label="OTP" prop="otp" class="half">
          <el-input v-model="form.otp" placeholder="Enter OTP" />
          <span v-if="errors.otp" class="error">{{ errors.otp }}</span>
        </el-form-item>

        <el-form-item label="Password" prop="password">
          <el-input
            type="password"
            v-model="form.password"
            placeholder="********"
            show-password
            autocomplete="new-password"
          />
          <span v-if="errors.password" class="error">{{ errors.password }}</span>
        </el-form-item>

        <el-form-item label="Confirm Password" prop="confirmPassword">
          <el-input
            type="password"
            v-model="form.confirmPassword"
            placeholder="********"
            show-password
            autocomplete="new-confirm-password"
          />
          <span v-if="errors.confirmPassword" class="error">{{ errors.confirmPassword }}</span>
        </el-form-item>

        <el-button
          type="primary"
          round
          class="submit-button"
          :loading="loading"
          :style="getButtonStyles"
          @click="submitForm"
        >
          {{ loading ? "Submitting..." : "Verify & Set Password" }}
        </el-button>

        <p v-if="successMessage" class="success">{{ successMessage }}</p>
        <p v-if="apiError" class="error">{{ apiError }}</p>

        <!-- 🔔 Resend OTP Section -->
        <div class="resend-otp">
          <el-button
            type="text"
            @click="resendOtp"
            :disabled="resending || cooldown > 0"
            :style="{ color: getPrimaryColor.color }"
          >
            {{ cooldown > 0 ? `Resend OTP in ${cooldown}s` : resending ? "Sending..." : "Resend OTP" }}
          </el-button>
          <span v-if="resendMessage" class="success">{{ resendMessage }}</span>
          <span v-if="resendError" class="error">{{ resendError }}</span>
        </div>
      </el-form>
    </el-card>

    <accessdenied v-else-if="pageType === 2 && !loading" :errorMessage="errorMessage" />
  </div>
</template>

<script>
import axios from "@/config/axios";
import CustomizationLayer from "@/mixins/CustomizationLayer";

export default {
  name: "OtpVerificationForm",
  components: {
    accessdenied: () => import("./accessdenied.vue"),
    AwesomeLoader: () => import("./loader.vue"),
  },
  mixins: [CustomizationLayer],
  data() {
    return {
      loaderMessage: "Loading ......!",
      pageType: 0,
      config: {
        overlayColor: "rgba(0, 0, 0, 0.2)",
      },
      form: {
        otp: "",
        password: "",
        confirmPassword: "",
        tenantCode: this.$route.params.unique_code,
      },
      rules: {
        otp: [
          { required: true, message: "OTP is required", trigger: "blur" },
          { min: 6, message: "OTP must be at least 6 characters", trigger: "blur" },
        ],
        password: [
          { required: true, message: "Password is required", trigger: "blur" },
          { min: 6, message: "Password must be at least 6 characters", trigger: "blur" },
        ],
        confirmPassword: [
          { required: true, message: "Please confirm your password", trigger: "blur" },
          {
            validator: (rule, value, callback) => {
              value !== this.form.password
                ? callback(new Error("Passwords do not match"))
                : callback();
            },
            trigger: "blur",
          },
        ],
      },
      errors: {},
      successMessage: "",
      apiError: "",
      loading: false,
      errorMessage: "",
      registration_config: null,
      company_info: null,

      // Resend OTP State
      resending: false,
      resendMessage: "",
      resendError: "",
      cooldown: 0,
      cooldownTimer: null,
    };
  },
  mounted() {
    document.documentElement.style.setProperty("--overlay-color", this.config.overlayColor);
    this.validateCode();
  },
  methods: {
    async validateCode() {
      if (!this.form.tenantCode) return;
      this.loading = true;
      this.loaderMessage = "Verifying URL... ";
      try {
        const res = await axios.get(`/ap/verify/${this.form.tenantCode}`);
        this.registration_config = res.data.data;
        this.company_info = res.data.data.company_id;
        this.pageType = 1;
      } catch (err) {
        this.pageType = 2;
        this.errorMessage = err?.response?.data?.message || err?.message;
      } finally {
        this.loading = false;
        this.loaderMessage = "Loading ......!";
      }
    },

    async submitForm() {
      this.$refs.formRef.validate(async (valid) => {
        if (!valid) return;
        this.loading = true;
        this.loaderMessage = "Validating the data... ";
        this.apiError = "";
        this.successMessage = "";
        try {
          const res = await axios.post("/application/user-email-verify", {
            contact_id: this.$route.query?.id,
            email: this.$route.query?.mail,
            verification_code: this.form.otp,
            password: this.form.password,
          });

          if (res.data) {
            this.successMessage = "Your account has been verified and password set.";
            this.$router.push({ path: "/signin/tenant" });
          } else {
            this.apiError = res.data.message || "Something went wrong.";
          }
        } catch (err) {
          this.apiError = err?.response?.data?.message || "Server error.";
        } finally {
          this.loading = false;
          this.loaderMessage = "Loading ......!";
        }
      });
    },

    // 🔔 Resend OTP Method
 async resendOtp() {
  this.resending = true;
  this.resendMessage = "";
  this.resendError = "";
  try {
    const payload = {
      contact_id: this.$route.query?.id,
      email: this.$route.query?.mail,
    };

    const res = await axios.post(`/ap/reg/resend/${this.form.tenantCode}`, payload);
    this.resendMessage = res?.data?.message || "OTP resent successfully.";
    this.startCooldown(); // Start 30s cooldown
  } catch (err) {
    this.resendError = err?.response?.data?.message || "Failed to resend OTP.";
  } finally {
    this.resending = false;
  }
},

    startCooldown() {
      this.cooldown = 30;
      if (this.cooldownTimer) clearInterval(this.cooldownTimer);
      this.cooldownTimer = setInterval(() => {
        this.cooldown--;
        if (this.cooldown <= 0) clearInterval(this.cooldownTimer);
      }, 1000);
    },
  },
  beforeUnmount() {
    if (this.cooldownTimer) clearInterval(this.cooldownTimer);
  },
};
</script>

<style scoped>
.w-full-75 {
    width: 55% !important;

}

.el-card__body,
.el-main {
    padding: 0 !important;
}

.el-form-item {
    margin-bottom: 5px;
}

.register-card {
    width: 100%;
    border-radius: 12px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
}

.logo-container {
    text-align: center;
    margin-bottom: 18px;
}

.sub-title {
    font-size: 13px;
    color: #777;
    margin-top: 2px;
}

.row {
    display: flex;
    justify-content: space-between;
    gap: 10px;
}

.half {
    flex: 1;
}

.submit-button {
    width: 100%;
    margin-top: 10px;
}

.logo {
    width: 10em;
    border-radius: 1em;
    border: 1px solid #bbbbbb45;
    padding: 0.5em;
    margin: 0.5em;
}

@media (max-width: 1024px) {
    .logo {
        width: 10em;
    }
    .w-full-75 {
    width: 50% !important;
    
}
}
@media (max-width: 768) {
    .logo {
        width: 8em;
    }
    .w-full-75 {
    width: 75% !important;
    
}
}
@media (max-width: 620px) {
    .w-full-75 {
    width: 85% !important;
    
}
}

@media (max-width: 480px) {
    .logo {
        width: 8em;
    }
    .w-full-75 {
    width: 95% !important;
    
}
}

.error {
    color: red;
    font-size: 13px;
}

.success {
    color: green;
    font-size: 14px;
    margin-top: 10px;
}
.resend-otp {
  text-align: center;
  margin-top: 10px;
}
</style>
